import React, { useState, useEffect } from 'react';
import { HelpingHand, Search, CheckCircle, XCircle, MessageSquare, AlertCircle, Send, Filter } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '../../lib/supabase';

interface GuidanceRequest {
  id: string;
  category: string;
  description: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  status: 'novo' | 'em_andamento' | 'respondido';
  created_at: string;
  response?: string;
  response_by?: string;
  response_at?: string;
}

// Define the categories for prayer requests
const PRAYER_CATEGORIES = [
  { id: 'personal', label: 'Pessoal' },
  { id: 'family', label: 'Família' },
  { id: 'health', label: 'Saúde' },
  { id: 'financial', label: 'Financeiro' },
  { id: 'spiritual', label: 'Espiritual' },
  { id: 'professional', label: 'Profissional' },
  { id: 'other', label: 'Outro' }
];

export default function AdminSpiritualGuidance() {
  const [requests, setRequests] = useState<GuidanceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<GuidanceRequest | null>(null);
  const [responseText, setResponseText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('novo');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  const perPage = 10;

  useEffect(() => {
    fetchRequests();
  }, [currentPage, statusFilter, categoryFilter]);

  async function fetchRequests() {
    try {
      setLoading(true);
      setError('');
      
      // Start building the query
      let query = supabase
        .from('spiritual_guidance_requests')
        .select('*', { count: 'exact' });
      
      // Apply status filter
      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }
      
      // Apply category filter
      if (categoryFilter) {
        query = query.eq('category', categoryFilter);
      }
      
      // Apply search term if present
      if (searchTerm) {
        query = query.or(`description.ilike.%${searchTerm}%,user_name.ilike.%${searchTerm}%,user_email.ilike.%${searchTerm}%`);
      }
      
      // Apply sorting - newest first, but prioritize new requests
      if (statusFilter === 'novo') {
        query = query.order('created_at', { ascending: false });
      } else {
        query = query.order('status', { ascending: true }).order('created_at', { ascending: false });
      }
      
      // Apply pagination
      const from = (currentPage - 1) * perPage;
      const to = from + perPage - 1;
      query = query.range(from, to);
      
      // Execute the query
      const { data, error: fetchError, count } = await query;
      
      if (fetchError) throw fetchError;
      
      setRequests(data || []);
      setTotalCount(count || 0);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateStatus(id: string, status: 'novo' | 'em_andamento' | 'respondido') {
    try {
      setError('');
      
      const { error } = await supabase
        .from('spiritual_guidance_requests')
        .update({ status })
        .eq('id', id);
      
      if (error) throw error;
      
      setSuccess(`Status atualizado para ${getStatusLabel(status)}`);
      
      // Update local state
      setRequests(prev => 
        prev.map(req => 
          req.id === id ? { ...req, status } : req
        )
      );
      
      if (selectedRequest && selectedRequest.id === id) {
        setSelectedRequest({ ...selectedRequest, status });
      }
      
      // Refresh the list
      fetchRequests();
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar status');
    }
  }

  async function handleSendResponse() {
    if (!selectedRequest) return;
    
    if (!responseText.trim()) {
      setError('Por favor, digite uma resposta');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');
      
      const { error } = await supabase
        .from('spiritual_guidance_requests')
        .update({
          response: responseText,
          response_by: user.id,
          response_at: new Date().toISOString(),
          status: 'respondido'
        })
        .eq('id', selectedRequest.id);
      
      if (error) throw error;
      
      setSuccess('Resposta enviada com sucesso!');
      setResponseText('');
      
      // Update local state
      const updatedRequest = {
        ...selectedRequest,
        response: responseText,
        response_by: user.id,
        response_at: new Date().toISOString(),
        status: 'respondido'
      };
      
      setSelectedRequest(updatedRequest);
      
      setRequests(prev => 
        prev.map(req => 
          req.id === selectedRequest.id ? updatedRequest : req
        )
      );
      
      // Refresh the list
      fetchRequests();
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar resposta');
    } finally {
      setLoading(false);
    }
  }

  function getStatusLabel(status: string) {
    switch (status) {
      case 'novo': return 'Novo';
      case 'em_andamento': return 'Em análise';
      case 'respondido': return 'Respondido';
      default: return status;
    }
  }

  function getStatusColor(status: string) {
    switch (status) {
      case 'novo': return 'bg-blue-100 text-blue-800';
      case 'em_andamento': return 'bg-yellow-100 text-yellow-800';
      case 'respondido': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  function getCategoryLabel(categoryId: string) {
    const category = PRAYER_CATEGORIES.find(cat => cat.id === categoryId);
    return category ? category.label : categoryId;
  }

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <AdminLayout title="Orientação Espiritual">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Lista de pedidos */}
        <div className="md:col-span-1 bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-brand-teal flex items-center gap-2">
              <HelpingHand className="h-5 w-5" />
              Pedidos
            </h2>
            <span className="text-sm text-gray-500">
              {totalCount} pedido(s)
            </span>
          </div>
          
          {/* Filtros */}
          <div className="mb-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar pedidos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    fetchRequests();
                  }
                }}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
              />
            </div>
            
            <div className="flex gap-2">
              <div className="flex-1">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal text-sm"
                >
                  <option value="">Todos os status</option>
                  <option value="novo">Novos</option>
                  <option value="em_andamento">Em análise</option>
                  <option value="respondido">Respondidos</option>
                </select>
              </div>
              
              <div className="flex-1">
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal text-sm"
                >
                  <option value="">Todas as categorias</option>
                  {PRAYER_CATEGORIES.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <button
                onClick={fetchRequests}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                title="Aplicar filtros"
              >
                <Filter className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Lista de pedidos */}
          {loading && requests.length === 0 ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Nenhum pedido encontrado
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {requests.map(request => (
                <div
                  key={request.id}
                  onClick={() => setSelectedRequest(request)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedRequest?.id === request.id
                      ? 'border-brand-teal bg-brand-light'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-medium text-gray-900">
                      {request.user_name}
                    </h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(request.status)}`}>
                      {getStatusLabel(request.status)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-1">
                    {getCategoryLabel(request.category)}
                  </p>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {request.description}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(request.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ))}
            </div>
          )}
          
          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-full disabled:opacity-50 hover:bg-gray-100"
                >
                  &lt;
                </button>
                
                <span className="text-sm">
                  Página {currentPage} de {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-full disabled:opacity-50 hover:bg-gray-100"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Detalhes do pedido */}
        <div className="md:col-span-2">
          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
              <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}
          
          {selectedRequest ? (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-brand-teal">
                    {getCategoryLabel(selectedRequest.category)}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Enviado em {new Date(selectedRequest.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(selectedRequest.status)}`}>
                  {getStatusLabel(selectedRequest.status)}
                </span>
              </div>
              
              {/* Informações do solicitante */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Informações do Solicitante</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Nome:</span>
                    <p className="font-medium">{selectedRequest.user_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{selectedRequest.user_email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Telefone:</span>
                    <p className="font-medium">{selectedRequest.user_phone}</p>
                  </div>
                </div>
              </div>
              
              {/* Descrição do pedido */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-2">Descrição do Pedido</h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-line">{selectedRequest.description}</p>
                </div>
              </div>
              
              {/* Resposta (se existir) */}
              {selectedRequest.response && (
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-2">Resposta</h3>
                  <div className="p-4 bg-brand-light rounded-lg">
                    <p className="text-gray-700 whitespace-pre-line">{selectedRequest.response}</p>
                    {selectedRequest.response_at && (
                      <p className="text-sm text-gray-500 mt-2">
                        Respondido em {new Date(selectedRequest.response_at).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Ações */}
              <div className="space-y-4">
                {/* Botões de status */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'novo')}
                    disabled={selectedRequest.status === 'novo'}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      selectedRequest.status === 'novo'
                        ? 'bg-blue-100 text-blue-800 cursor-default'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Marcar como Novo
                  </button>
                  
                  <button
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'em_andamento')}
                    disabled={selectedRequest.status === 'em_andamento'}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      selectedRequest.status === 'em_andamento'
                        ? 'bg-yellow-100 text-yellow-800 cursor-default'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Marcar como Em Análise
                  </button>
                  
                  <button
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'respondido')}
                    disabled={selectedRequest.status === 'respondido'}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      selectedRequest.status === 'respondido'
                        ? 'bg-green-100 text-green-800 cursor-default'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Marcar como Respondido
                  </button>
                </div>
                
                {/* Formulário de resposta */}
                {selectedRequest.status !== 'respondido' && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Enviar Resposta</h3>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      rows={5}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal mb-3"
                      placeholder="Digite sua resposta para o solicitante..."
                    />
                    <button
                      onClick={handleSendResponse}
                      disabled={loading || !responseText.trim()}
                      className="bg-brand-teal text-white px-4 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          <span>Enviando...</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          <span>Enviar Resposta</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-6 flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <HelpingHand className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Selecione um pedido
                </h3>
                <p className="text-gray-500 max-w-md">
                  Escolha um pedido da lista à esquerda para visualizar os detalhes e responder.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}